import React, { useState, useRef } from 'react';
import entireLogo from '../assets/entire-logo.png';
import { useLang } from '../i18n/useLang';

const servicesConfig = [
  { icon: '🏥', page: 'services', section: 'pillar-health' },
  { icon: '🤖', page: 'services', section: 'pillar-agentic' },
  { icon: '🧠', page: 'services', section: 'pillar-ml' },
  { icon: '🗄️', page: 'services', section: 'pillar-dataset' },
  { icon: '🔍', page: 'services', section: 'pillar-explainability' },
  { icon: '💡', page: 'services', section: 'section-vision' },
];

const resourcesConfig = [
  { icon: '📝', page: 'blog', section: 'blog-articles' },
  { icon: '⚡', page: 'learn', section: 'learn-formats' },
  { icon: '📊', page: 'blog', section: 'blog-articles' },
  { icon: '🔬', page: 'blog', section: 'blog-articles' },
];

const linkCls = (active, lang) =>
  `block px-[6px] xl:px-[9px] py-1.5 text-[clamp(10px,0.85vw,${lang === 'fr' ? '13px' : '13px'})] font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap hover:text-nf-text hover:bg-white/[0.04] ${active ? 'text-nf-acc' : 'text-nf-t2'}`;

const THEMES = [
  { key: 'cosmic', dot: '#6d28d9', label: 'Cosmic' },
  { key: 'ocean', dot: '#1e5fb0', label: 'Ocean' },
  { key: 'onyx', dot: '#5a5a5a', label: 'Onyx' },
  { key: 'forest', dot: '#0c7a58', label: 'Forest' },
  { key: 'dusk', dot: '#b8b0d8', label: 'Dusk' },
];

export default function Nav({ currentPage, navigate, lang, setLang, theme, setTheme }) {
  const { t } = useLang();
  const servicesItems = servicesConfig.map((cfg, i) => ({ ...cfg, label: t('nav.servicesLabels')[i] }));
  const resourcesItems = resourcesConfig.map((cfg, i) => ({ ...cfg, label: t('nav.resourcesLabels')[i] }));
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const servicesTimer = useRef(null);
  const resourcesTimer = useRef(null);
  const themeTimer = useRef(null);

  const openServices = () => { clearTimeout(servicesTimer.current); setServicesOpen(true); };
  const closeServices = () => { servicesTimer.current = setTimeout(() => setServicesOpen(false), 400); };
  const openResources = () => { clearTimeout(resourcesTimer.current); setResourcesOpen(true); };
  const closeResources = () => { resourcesTimer.current = setTimeout(() => setResourcesOpen(false), 400); };
  const openTheme = () => { clearTimeout(themeTimer.current); setThemeOpen(true); };
  const closeTheme = () => { themeTimer.current = setTimeout(() => setThemeOpen(false), 350); };

  const dropdownCls = (open) =>
    `absolute top-full left-1/2 -translate-x-1/2 bg-nf-bg3 border border-[rgba(109,40,217,0.22)] rounded-xl pt-5 pb-3 px-3 z-[300] transition-all duration-200 ${open ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-2'}`;

  const go = (page, section) => {
    navigate(page);
    setMobileOpen(false); setServicesOpen(false); setResourcesOpen(false); setMobileServicesOpen(false); setMobileResourcesOpen(false); setThemeOpen(false);
    if (section) setTimeout(() => document.getElementById(section)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-[200] backdrop-blur-xl border-b transition-colors duration-300 bg-nf-bg/[0.88] ${theme === 'dusk' ? 'border-black/[0.10]' : 'border-white/[0.06]'}`}>
      <div className="flex items-center justify-between px-4 sm:px-8 lg:px-20 h-[80px] w-full">
        {/* Logo */}
        <a className="flex items-center cursor-pointer shrink-0" onClick={() => go('home')}>
          <div style={theme === 'dusk' ? { background: 'linear-gradient(135deg, rgba(12,8,32,0.55), rgba(6,4,20,0.45))', borderRadius: 10, padding: '2px 10px' } : {}}>
            <img
              src={entireLogo}
              alt="NovaFrik AI"
              style={{ height: 72, width: 'auto', display: 'block', objectFit: 'contain', mixBlendMode: 'screen', filter: 'brightness(1.05)' }}
            />
          </div>
        </a>

        {/* Desktop Links */}
        <ul className="hidden lg:flex items-center list-none">
          <li>
            <a className={linkCls(currentPage === 'home', lang)} onClick={() => go('home')}>{t('nav.home')}</a>
          </li>

          {/* Services dropdown */}
          <li className="relative">
            <a className={linkCls(currentPage === 'services', lang)} onMouseEnter={openServices} onMouseLeave={closeServices}>{t('nav.services')} ▾</a>
            <div className={dropdownCls(servicesOpen)} onMouseEnter={openServices} onMouseLeave={closeServices}>
              <div className="flex items-stretch gap-1">
                {servicesItems.filter(i => !i.divider).map((item, i) => (
                  <a key={i}
                    className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl text-nf-t2 cursor-pointer transition-all hover:bg-nf-surface hover:text-nf-text min-w-[110px] text-center"
                    onClick={() => go(item.page, item.section)}>
                    <span className="text-[26px]">{item.icon}</span>
                    <span className={`text-[13px] font-medium leading-tight text-center ${lang === 'fr' ? '' : 'whitespace-nowrap'}`}>{item.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </li>

          <li>
            <a className={linkCls(currentPage === 'about', lang)} onClick={() => go('about')}>{t('nav.aboutUs')}</a>
          </li>

          <li>
            <a className={linkCls(currentPage === 'offers', lang)} onClick={() => go('offers')}>{t('nav.offers')}</a>
          </li>

          <li>
            <a className={linkCls(currentPage === 'learn', lang)} onClick={() => go('learn')}>{t('nav.learn')}</a>
          </li>

          {/* Resources dropdown */}
          <li className="relative">
            <a className={linkCls(currentPage === 'blog', lang)} onMouseEnter={openResources} onMouseLeave={closeResources}>{t('nav.resources')} ▾</a>
            <div className={dropdownCls(resourcesOpen)} onMouseEnter={openResources} onMouseLeave={closeResources}>
              <div className="flex items-stretch gap-1">
                {resourcesItems.map((item, i) => (
                  <a key={i}
                    className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl text-nf-t2 cursor-pointer transition-all hover:bg-nf-surface hover:text-nf-text min-w-[110px] text-center"
                    onClick={() => go(item.page, item.section)}>
                    <span className="text-[26px]">{item.icon}</span>
                    <span className={`text-[13px] font-medium leading-tight text-center ${lang === 'fr' ? '' : 'whitespace-nowrap'}`}>{item.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </li>

          <li>
            <a className={linkCls(false, lang)} onClick={() => go('about')}>{t('nav.contact')}</a>
          </li>

          <li>
            <a className={linkCls(currentPage === 'faq', lang)} onClick={() => go('faq')}>{t('nav.faq')}</a>
          </li>
        </ul>

        {/* Desktop actions */}
        <div className="hidden lg:flex items-center gap-1.5 shrink-0">
          {/* Theme selector — dropdown (dots only) */}
          <div className="relative" onMouseEnter={openTheme} onMouseLeave={closeTheme}>
            <button
              title={THEMES.find(th => th.key === theme)?.label}
              aria-label="Change theme"
              className="flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors shrink-0"
              style={{ border: theme === 'dusk' ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(255,255,255,0.12)' }}>
              <span className="w-[9px] h-[9px] rounded-full shrink-0" style={{ backgroundColor: THEMES.find(th => th.key === theme)?.dot }} />
              <span className={`text-[8px] text-nf-t2 opacity-60 transition-transform duration-200 ${themeOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            <div className={`absolute top-full right-0 mt-1.5 bg-nf-bg3 border border-[rgba(109,40,217,0.22)] rounded-xl py-2 px-2 z-[350] transition-all duration-200 ${themeOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-1'}`}>
              <div className="flex items-center gap-[7px]">
                {THEMES.map(th => (
                  <button
                    key={th.key}
                    onClick={() => { setTheme(th.key); setThemeOpen(false); }}
                    title={th.label}
                    aria-label={th.label}
                    className="w-[14px] h-[14px] rounded-full shrink-0 transition-transform duration-150 hover:scale-125"
                    style={{
                      backgroundColor: th.dot,
                      outline: theme === th.key ? '1.5px solid rgba(255,255,255,0.8)' : '1.5px solid transparent',
                      outlineOffset: '2px',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          {/* Language toggle — single button */}
          <button
            onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
            className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-bold rounded-md transition-colors text-nf-t2 hover:text-nf-text"
            style={{ border: theme === 'dusk' ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(255,255,255,0.12)' }}>
            <span className="text-[10px] opacity-60">🌐</span>
            {lang.toUpperCase()}
          </button>
          <a className="btn-ghost !text-[12px] !px-3 !py-1.5">{t('nav.login')}</a>
          <a className="btn-cta !text-[12px] !px-3 !py-1.5" onClick={() => go('learn')}>{t('nav.bookSession')}</a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg text-nf-t2 hover:bg-white/[0.06] transition-colors text-xl"
          onClick={() => setMobileOpen(o => !o)}
          aria-label="Toggle menu">
          {mobileOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className={`lg:hidden border-t px-4 py-4 flex flex-col gap-1 max-h-[calc(100vh-80px)] overflow-y-auto transition-colors duration-300 bg-nf-bg/[0.98] ${theme === 'dusk' ? 'border-black/[0.10]' : 'border-white/[0.06]'}`}>

          {/* Home */}
          <a className={`block px-4 py-3 rounded-lg text-[19px] font-medium transition-colors cursor-pointer ${currentPage === 'home' ? 'text-nf-acc bg-nf-p/[0.08]' : 'text-nf-t2 hover:text-nf-text hover:bg-white/[0.04]'}`}
            onClick={() => go('home')}>{t('nav.home')}</a>

          {/* Services accordion */}
          <div>
            <button
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-[19px] font-medium transition-colors cursor-pointer ${currentPage === 'services' ? 'text-nf-acc bg-nf-p/[0.08]' : 'text-nf-t2 hover:text-nf-text hover:bg-white/[0.04]'}`}
              onClick={() => setMobileServicesOpen(o => !o)}>
              <span>{t('nav.services')}</span>
              <span className={`text-[14px] transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {mobileServicesOpen && (
              <div className="ml-4 mt-1 mb-1 flex flex-col gap-0.5 border-l border-white/[0.06] pl-3">
                {servicesItems.map((item, i) =>
                  item.divider
                    ? <div key={i} className="h-px bg-white/[0.06] my-1" />
                    : (
                      <a key={i}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[17px] text-nf-t2 cursor-pointer transition-all hover:bg-nf-surface hover:text-nf-text"
                        onClick={() => go(item.page, item.section)}>
                        <span className="text-[16px]">{item.icon}</span>
                        {item.label}
                      </a>
                    )
                )}
              </div>
            )}
          </div>

          {/* About */}
          <a className={`block px-4 py-3 rounded-lg text-[19px] font-medium transition-colors cursor-pointer ${currentPage === 'about' ? 'text-nf-acc bg-nf-p/[0.08]' : 'text-nf-t2 hover:text-nf-text hover:bg-white/[0.04]'}`}
            onClick={() => go('about')}>{t('nav.aboutUs')}</a>

          {/* Offers */}
          <a className={`block px-4 py-3 rounded-lg text-[19px] font-medium transition-colors cursor-pointer ${currentPage === 'offers' ? 'text-nf-acc bg-nf-p/[0.08]' : 'text-nf-t2 hover:text-nf-text hover:bg-white/[0.04]'}`}
            onClick={() => go('offers')}>{t('nav.offers')}</a>

          {/* Learn */}
          <a className={`block px-4 py-3 rounded-lg text-[19px] font-medium transition-colors cursor-pointer ${currentPage === 'learn' ? 'text-nf-acc bg-nf-p/[0.08]' : 'text-nf-t2 hover:text-nf-text hover:bg-white/[0.04]'}`}
            onClick={() => go('learn')}>{t('nav.learn')}</a>

          {/* Resources accordion */}
          <div>
            <button
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-[19px] font-medium transition-colors cursor-pointer ${currentPage === 'blog' ? 'text-nf-acc bg-nf-p/[0.08]' : 'text-nf-t2 hover:text-nf-text hover:bg-white/[0.04]'}`}
              onClick={() => setMobileResourcesOpen(o => !o)}>
              <span>{t('nav.resources')}</span>
              <span className={`text-[14px] transition-transform duration-200 ${mobileResourcesOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {mobileResourcesOpen && (
              <div className="ml-4 mt-1 mb-1 flex flex-col gap-0.5 border-l border-white/[0.06] pl-3">
                {resourcesItems.map((item, i) => (
                  <a key={i}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[17px] text-nf-t2 cursor-pointer transition-all hover:bg-nf-surface hover:text-nf-text"
                    onClick={() => go(item.page, item.section)}>
                    <span className="text-[16px]">{item.icon}</span>
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <a className="block px-4 py-3 rounded-lg text-[19px] font-medium text-nf-t2 transition-colors cursor-pointer hover:text-nf-text hover:bg-white/[0.04]"
            onClick={() => go('about')}>{t('nav.contact')}</a>

          {/* FAQ */}
          <a className={`block px-4 py-3 rounded-lg text-[19px] font-medium transition-colors cursor-pointer ${currentPage === 'faq' ? 'text-nf-acc bg-nf-p/[0.08]' : 'text-nf-t2 hover:text-nf-text hover:bg-white/[0.04]'}`}
            onClick={() => go('faq')}>{t('nav.faq')}</a>

          <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-white/[0.06]">
            <div className="flex items-center justify-between gap-3">
              {/* Theme dots (mobile) */}
              <div className="flex items-center gap-[8px] px-[10px] py-[8px] rounded-[9px]" style={{ border: theme === 'dusk' ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(255,255,255,0.12)' }}>
                {THEMES.map(th => (
                  <button
                    key={th.key}
                    onClick={() => setTheme(th.key)}
                    title={th.label}
                    aria-label={th.label}
                    className="w-[16px] h-[16px] rounded-full shrink-0 transition-transform duration-150 active:scale-125"
                    style={{
                      backgroundColor: th.dot,
                      outline: theme === th.key ? '1.5px solid rgba(255,255,255,0.8)' : '1.5px solid transparent',
                      outlineOffset: '2px',
                    }}
                  />
                ))}
              </div>
              {/* Language toggle — single button */}
              <button
                onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
                className="flex items-center gap-1.5 px-3 py-2 text-[13px] font-bold rounded-lg text-nf-t2 hover:text-nf-text transition-colors"
                style={{ border: theme === 'dusk' ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(255,255,255,0.12)' }}>
                <span className="text-[11px] opacity-60">🌐</span>
                {lang.toUpperCase()}
              </button>
            </div>
            <a className="btn-ghost text-center">{t('nav.login')}</a>
            <a className="btn-cta text-center" onClick={() => go('learn')}>{t('nav.bookSession')}</a>
          </div>
        </div>
      )}
    </nav>
  );
}
